import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Badge, KeyRow, Panel, Stat, TabBar, stamp } from "./ui";
import { Trash2, Plus, X } from "lucide-react";

export default function TeamPage() {
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [msg, setMsg] = useState("");
  const [editingRole, setEditingRole] = useState(null);
  const [newMember, setNewMember] = useState(null);
  const [newRole, setNewRole] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([api.get("/admin/team"), api.get("/admin/roles")]);
      setMembers(t.data.members || []);
      setRoles(r.data.roles || []);
      setPermissions(r.data.permissions || {});
    } catch (e) { setMsg(errText(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, done = "Saved.") => {
    setMsg("");
    try { await fn(); setMsg(done); await load(); }
    catch (e) { setMsg(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-team-page">
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-ink">Team & roles</h1>
          <p className="text-sm text-muted">Who can see what inside Fleurinelle operations.</p>
        </div>
        <div className="ms-auto grid grid-cols-2 gap-3">
          <Stat testid="team-kpi-members" label="Members" value={members.length} />
          <Stat testid="team-kpi-roles" label="Roles" value={roles.length} />
        </div>
      </div>

      {msg ? <p className="text-sm text-brass" data-testid="team-message">{msg}</p> : null}

      <TabBar testid="team-tabs" value={tab} onChange={setTab}
              tabs={[["members", "Members", members.length], ["roles", "Roles", roles.length],
                     ["permissions", "Permission reference", Object.keys(permissions).length]]} />

      {tab === "members" ? (
        <div className="space-y-3">
          <button className="btn-primary text-xs" data-testid="team-new-member" onClick={() => setNewMember({ email: "", name: "", role: "operations", password: "" })}>
            <Plus size={12} className="inline me-1" /> Invite a teammate
          </button>
          <div className="overflow-x-auto border border-brass-light bg-white">
            <table className="w-full text-sm">
              <thead className="bg-plaster-deep">
                <tr>{["Member", "Role", "Permissions", "Added", ""].map((h) => (
                  <th key={h} className="text-start px-3 py-2 smallcaps whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-brass-light" data-testid={`team-row-${m.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-ink">{m.name}</div>
                      <div className="text-xs text-muted">{m.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <select className="ctl" value={m.role} data-testid={`team-role-${m.id}`}
                              onChange={(e) => act(() => api.patch(`/admin/team/${m.id}`, { role: e.target.value }), "Role updated.")}>
                        {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted max-w-md">
                      {(m.permissions || []).slice(0, 4).join(", ")}{(m.permissions || []).length > 4 ? `, +${m.permissions.length - 4} more` : ""}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{stamp(m.created_at)}</td>
                    <td className="px-3 py-2">
                      <button className="btn-ghost text-[11px]" data-testid={`team-remove-${m.id}`}
                              onClick={() => { if (window.confirm(`Remove ${m.name}?`)) act(() => api.delete(`/admin/team/${m.id}`), "Removed."); }}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 ? <tr><td colSpan={5} className="p-4 text-sm text-muted">No teammate yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === "roles" ? (
        <div className="space-y-3">
          <button className="btn-primary text-xs" data-testid="team-new-role" onClick={() => setNewRole({ id: "", name: "", description: "", permissions: [] })}>
            <Plus size={12} className="inline me-1" /> Create a role
          </button>
          <div className="grid md:grid-cols-2 gap-4">
            {roles.map((r) => (
              <Panel key={r.id} title={r.name} testid={`role-${r.id}`}
                     action={<Badge tone={r.is_system ? "ink" : "warn"}>{r.is_system ? "system" : "custom"}</Badge>}>
                <KeyRow label="ID"><code className="text-xs">{r.id}</code></KeyRow>
                <KeyRow label="Members">{r.members}</KeyRow>
                <p className="text-xs text-muted">{r.description}</p>
                <div className="text-xs text-muted">
                  {(r.permissions || []).length} permission{(r.permissions || []).length === 1 ? "" : "s"}
                </div>
                <label className="flex items-center gap-2 text-xs mt-1 cursor-pointer"
                       data-testid={`role-impersonate-${r.id}`}>
                  <input type="checkbox" checked={(r.permissions || []).includes("impersonate_florist")}
                         onChange={(e) => {
                           const next = e.target.checked
                             ? Array.from(new Set([...(r.permissions || []), "impersonate_florist"]))
                             : (r.permissions || []).filter((p) => p !== "impersonate_florist");
                           act(() => api.patch(`/admin/roles/${r.id}`,
                                                { id: r.id, name: r.name,
                                                  description: r.description || "",
                                                  permissions: next }),
                               e.target.checked
                                 ? `${r.name} can now sign in as any florist.`
                                 : `${r.name} can no longer sign in as florists.`);
                         }} />
                  <span>Impersonate shops (support agents fixing bouquets)</span>
                </label>
                <div className="flex gap-2">
                  <button className="btn-ghost text-[11px]" data-testid={`role-edit-${r.id}`}
                          onClick={() => setEditingRole({ ...r })}>Edit permissions</button>
                  {!r.is_system ? (
                    <button className="btn-ghost text-[11px] text-danger" data-testid={`role-delete-${r.id}`}
                            onClick={() => { if (window.confirm(`Delete role "${r.name}"?`)) act(() => api.delete(`/admin/roles/${r.id}`), "Deleted."); }}>
                      Delete
                    </button>
                  ) : null}
                </div>
              </Panel>
            ))}
          </div>
        </div>
      ) : null}

      {tab === "permissions" ? (
        <div className="overflow-x-auto border border-brass-light bg-white">
          <table className="w-full text-sm">
            <thead className="bg-plaster-deep"><tr>
              <th className="text-start px-3 py-2 smallcaps">Permission</th>
              <th className="text-start px-3 py-2 smallcaps">What it unlocks</th>
              <th className="text-start px-3 py-2 smallcaps">Roles that have it</th>
            </tr></thead>
            <tbody>
              {Object.entries(permissions).map(([key, desc]) => (
                <tr key={key} className="border-t border-brass-light" data-testid={`perm-${key}`}>
                  <td className="px-3 py-2"><code className="text-xs">{key}</code></td>
                  <td className="px-3 py-2 text-xs text-muted">{desc}</td>
                  <td className="px-3 py-2 text-xs">{roles.filter((r) => (r.permissions || []).includes(key)).map((r) => r.name).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editingRole ? (
        <RoleEditor role={editingRole} permissions={permissions} onClose={() => setEditingRole(null)}
                    onSave={(payload) => act(() => api.patch(`/admin/roles/${editingRole.id}`, payload), "Role updated.").then(() => setEditingRole(null))} />
      ) : null}

      {newMember ? (
        <NewMember roles={roles} draft={newMember} onClose={() => setNewMember(null)} onSave={(d) =>
          act(() => api.post("/admin/team", d), "Teammate added.").then(() => setNewMember(null))} />
      ) : null}

      {newRole ? (
        <NewRole permissions={permissions} draft={newRole} onClose={() => setNewRole(null)} onSave={(d) =>
          act(() => api.post("/admin/roles", d), "Role created.").then(() => setNewRole(null))} />
      ) : null}
    </div>
  );
}

const ModalShell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 scrim z-40 flex items-center justify-center p-6" onClick={onClose}>
    <div className="bg-plaster w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 border border-brass" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-3 border-b border-brass-light pb-3 mb-4">
        <h2 className="font-display text-2xl text-ink">{title}</h2>
        <button className="ms-auto" onClick={onClose}><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const PermGrid = ({ permissions, selected, toggle }) => (
  <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border border-brass-light p-3">
    {Object.entries(permissions).map(([key, desc]) => (
      <label key={key} className="flex items-start gap-2 text-xs" data-testid={`perm-toggle-${key}`}>
        <input type="checkbox" checked={selected.includes(key)} onChange={() => toggle(key)} />
        <span><b className="text-ink">{key}</b><br /><span className="text-muted">{desc}</span></span>
      </label>
    ))}
  </div>
);

function RoleEditor({ role, permissions, onClose, onSave }) {
  const [perms, setPerms] = useState(role.permissions || []);
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || "");
  const toggle = (k) => setPerms((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  return (
    <ModalShell title={`Edit "${role.name}"`} onClose={onClose}>
      <div className="space-y-3" data-testid="role-editor">
        {!role.is_system ? (
          <>
            <label className="block text-xs">Name<input className="field mt-1" value={name} data-testid="role-name" onChange={(e) => setName(e.target.value)} /></label>
            <label className="block text-xs">Description<input className="field mt-1" value={description} data-testid="role-description" onChange={(e) => setDescription(e.target.value)} /></label>
          </>
        ) : <p className="text-xs text-muted">System roles keep their name; only the permission set can change.</p>}
        <PermGrid permissions={permissions} selected={perms} toggle={toggle} />
        <button className="btn-primary w-full" data-testid="role-save" onClick={() => onSave({ id: role.id, name, description, permissions: perms })}>Save</button>
      </div>
    </ModalShell>
  );
}

function NewMember({ roles, draft, onClose, onSave }) {
  const [d, setD] = useState(draft);
  return (
    <ModalShell title="Invite a teammate" onClose={onClose}>
      <div className="space-y-3" data-testid="new-member">
        <input className="field" placeholder="Name" data-testid="new-member-name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        <input className="field" placeholder="Email" data-testid="new-member-email" value={d.email} onChange={(e) => setD({ ...d, email: e.target.value })} />
        <select className="field" data-testid="new-member-role" value={d.role} onChange={(e) => setD({ ...d, role: e.target.value })}>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <input className="field" type="password" placeholder="Starting password (share privately)" data-testid="new-member-password" value={d.password} onChange={(e) => setD({ ...d, password: e.target.value })} />
        <button className="btn-primary w-full" data-testid="new-member-save" onClick={() => onSave(d)}>Add member</button>
      </div>
    </ModalShell>
  );
}

function NewRole({ permissions, draft, onClose, onSave }) {
  const [d, setD] = useState(draft);
  const toggle = (k) => setD({ ...d, permissions: d.permissions.includes(k) ? d.permissions.filter((x) => x !== k) : [...d.permissions, k] });
  return (
    <ModalShell title="Create a role" onClose={onClose}>
      <div className="space-y-3" data-testid="new-role">
        <input className="field" placeholder="Role id (e.g. moderator)" data-testid="new-role-id"
               value={d.id} onChange={(e) => setD({ ...d, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} />
        <input className="field" placeholder="Display name" data-testid="new-role-name" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        <input className="field" placeholder="Short description" data-testid="new-role-description" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} />
        <PermGrid permissions={permissions} selected={d.permissions} toggle={toggle} />
        <button className="btn-primary w-full" data-testid="new-role-save" onClick={() => onSave(d)}>Create</button>
      </div>
    </ModalShell>
  );
}
